import React from 'react'
import type { PageConfig } from 'models/pages'
import { getCookieValue } from 'utils/getCookieValue'
import { OPTIMIZELY_EXPOSURE_FLAGS } from 'models/optimizely'
import { useInternalAnalytics, buildMParticleGlobalProperties } from '.'

/**
 * Logs an "Experiment Viewed" event to mParticle once per unique experiment/variation.
 * - Waits until mParticle is available and consent is synced
 * - Reads the flattened OptimizelyContext from PageConfig
 * - Only logs for flags listed in testExposures; other flags are skipped
 * - For each decision that is enabled and has a non-null variationKey, logs the event with:
 *   experiment_name = flagKey, variation_name = variationKey, and value = flagKey (for reporting)
 *   plus global/donation props and GA4 attrs
 * - Uses an in-memory Set to de-duplicate logs for the lifetime of this page
 */

export const useMParticleLogExperimentViewed = (pageConfig: PageConfig, loggedInState: boolean) => {
  const { mParticle, consentSynced } = useInternalAnalytics()

  /**
   * De-duplication across the page lifetime.
   * Each "flagKey|variationKey" pair is logged at most once while this component is mounted.
   * If you need to re-log when a variation changes, prefer a diff-based approach instead.
   */

  const seenRef = React.useRef(new Set<string>())

  React.useEffect(() => {
    if (!mParticle || !consentSynced) return

    const optimizelyContext = pageConfig.optimizelyContext
    if (!optimizelyContext) return

    /**
     * Precompute stable values once per effect run:
     * - globalProperties: global analytics attrs
     * - distinctId: cookie lookup
     * - GA4 attrs: title/location/referrer
     * This avoids repeated lookups inside the loop and ensures consistent values across events.
     */
    const globalProperties = buildMParticleGlobalProperties(pageConfig, mParticle, loggedInState)
    const distinctId = getCookieValue('pgdid')
    const ga4Attrs = {
      'GA4.Title': document.title,
      'GA4.Location': document.location.href,
      'GA4.Referrer': document.referrer,
    }

    /**
     * Iterate the flattened Optimizely context and log only concrete, enabled variations.
     * variationKey must be truthy (e.g., "control", "treatment"), otherwise skip.
     */

    for (const [flagKey, decision] of Object.entries(optimizelyContext)) {
      // * Skip this entry if test exposure doesn't target any Whammy pages.
      if (!OPTIMIZELY_EXPOSURE_FLAGS.includes(flagKey)) continue
      // * Skip this entry if experiment is not enabled or user is not in treatment variant.
      if (!decision.enabled || !decision.variationKey) continue
      // * Skip this entry if we have already logged this test treatment.
      const dedupeKey = `${flagKey}|${decision.variationKey}`
      if (seenRef.current.has(dedupeKey)) continue

      seenRef.current.add(dedupeKey)

      mParticle.logEvent(
        'Experiment Viewed',
        mParticle.EventType.Other,
        {
          experiment_name: flagKey,
          variation_name: decision.variationKey,
          distinct_id: distinctId,
          ...globalProperties,
        },
        ga4Attrs,
      )
    }
  }, [consentSynced, loggedInState, mParticle, pageConfig])
}
